<template>
  <input
    v-money="{ precision, decimal, thousands, prefix, suffix }"
    v-bind="$attrs"
    class="z-input__field v-money"
    :class="{ 'z-input__field--has-error': isInvalid() }"
    :value="formattedValue"
    type="tel"
    @change="change"
    v-on="forwardableListeners"
  >
</template>
<script>
export default {
  name: 'Money',
  props: {
    value: {
      required: true,
      type: [Number, String],
      default: 0,
    },
    masked: {
      type: Boolean,
      default: false,
    },
    precision: {
      type: Number,
      default: 2,
    },
    decimal: {
      type: String,
      default: ',',
    },
    thousands: {
      type: String,
      default: '.',
    },
    prefix: {
      type: String,
      default: 'R$ ',
    },
    suffix: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    errors: {
      type: Array,
      default: () => [],
    },
    autofocusEnabled: {
      type: Boolean,
      default: false,
    },
    min: {
      type: Number,
      default: () => 0.0,
    },
    max: {
      type: Number,
      default: () => 10000000,
    },
  },
  data() {
    return {
      formattedValue: '',
    };
  },
  computed: {
    forwardableListeners() {
      const listeners = { ...this.$listeners };
      delete listeners.input;
      return listeners;
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        const formatted = this.format(newValue, this.$props);
        if (formatted !== this.formattedValue) {
          this.formattedValue = formatted;
        }
      },
    },
  },
  methods: {
    format(inputParam, opt) {
      let input = inputParam;
      if (!input) return '';
      if (typeof input === 'number') {
        input = input.toFixed(this.fixed(opt.precision));
      }
      const negative = input.indexOf('-') >= 0 ? '-' : '';
      const numbers = this.onlyNumbers(input);
      const currency = this.numbersToCurrency(numbers, opt.precision);
      const parts = this.toStr(currency).split('.');
      let integer = parts[0];
      const decimal = parts[1];
      integer = this.addThousandSeparator(integer, opt.thousands);
      return opt.prefix + negative + this.joinIntegerAndDecimal(integer, decimal, opt.decimal) + opt.suffix;
    },
    joinIntegerAndDecimal(integer, decimal, separator) {
      return decimal ? integer + separator + decimal : integer;
    },
    addThousandSeparator(integer, separator) {
      return integer.replace(/(\d)(?=(?:\d{3})+\b)/gm, `$1${separator}`);
    },
    change(evt) {
      const unMaskedValue = this.unformat(evt.target.value, this.precision);
      if (this.min !== null && unMaskedValue < this.min) {
        this.formattedValue = this.format(this.min, this.$props);
      } else if (this.max !== null && unMaskedValue > this.max) {
        this.formattedValue = this.format(Math.floor(unMaskedValue * 10) / 100, this.$props);
      }
      this.$emit('input', this.unformat(this.formattedValue, this.precision));
    },
    unformat(input, precision) {
      const negative = input.indexOf('-') >= 0 ? -1 : 1;
      const numbers = this.onlyNumbers(input);
      const currency = this.numbersToCurrency(numbers, precision);
      return parseFloat(currency) * negative;
    },
    numbersToCurrency(numbers, precision) {
      const exp = 10 ** precision;
      const float = parseFloat(numbers) / exp;
      return float.toFixed(this.fixed(precision));
    },
    fixed(precision) {
      return this.between(0, precision, 20);
    },
    between(min, n, max) {
      return Math.max(min, Math.min(n, max));
    },
    onlyNumbers(input) {
      return this.toStr(input).replace(/\D+/g, '') || '0';
    },
    toStr(value) {
      return value ? value.toString() : '';
    },
    isInvalid() {
      return this.errors.length > 0;
    },
  },
};
</script>
